import { SelMessage } from "@selector/shared/components";
import { type FC, useContext, useMemo } from "react";
import { Card, CardRowSimple, type GridCard, LoadingSpinner } from "widgets";
import { formatNumberByThousands } from "widgets/utils";

import { type AaFeatureStats, type Organism } from "@/graphql.ts";
import { GenomicAnnotationsContext } from "@/modules/summary-pages/AnnotationsContext.ts";
import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";

export interface IGenomicProteinContentCardProps extends GridCard {
  genomeId: Organism["id"];
}

const GenomicProteinContentCard: FC<IGenomicProteinContentCardProps> = () => {
  const {
    proteinStatistics: { loading, error, data: genomeStatistics },
  } = useContext(GenomicAnnotationsContext);

  const aaFeaturesCount = useMemo<AaFeatureStats[]>(
    () => (genomeStatistics?.aaFeaturesCount ?? []) as unknown as AaFeatureStats[],
    [genomeStatistics?.aaFeaturesCount],
  );
  const proteinsCount = useMemo<number>(() => genomeStatistics?.proteinsCount ?? 0, [genomeStatistics?.proteinsCount]);

  return (
    <Card key="genome-info-card" title={"Protein Features"}>
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        // Fallback. Usually graphql errors are handled globally
        <ContentPlaceholder>{error?.message}</ContentPlaceholder>
      ) : aaFeaturesCount.length === 0 && proteinsCount === 0 ? (
        <SelMessage id={"noContent.proteinContent"} />
      ) : (
        <>
          <CardRowSimple
            label=""
            value={["Motifs Assigned", "Percentage", "With Motif", "Motif Coverage"]}
            key={"card-row-motif-labels"}
          />
          {aaFeaturesCount.map((aaFeatureCount) => (
            <CardRowSimple
              label={aaFeatureCount.featureTypeName}
              value={[
                <span key={"count"}>{formatNumberByThousands(aaFeatureCount.count)}</span>,
                <span key={"percentage"}>
                  {proteinsCount !== 0 ? ((aaFeatureCount.count / proteinsCount) * 100).toFixed(2) + "%" : "-"}
                </span>,
                <span key={"with-motif"}>{formatNumberByThousands(aaFeatureCount.associatedProteins)}</span>,
                <span key={"motif-coverage"}>
                  {proteinsCount !== 0
                    ? ((aaFeatureCount.associatedProteins / proteinsCount) * 100).toFixed(2) + "%"
                    : "-"}
                </span>,
              ]}
              key={"card-row-motif-" + aaFeatureCount.featureTypeName}
            />
          ))}
        </>
      )}
    </Card>
  );
};

export { GenomicProteinContentCard };
